import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Mail, MessageSquare, Send, ShieldCheck, Loader2, Info, Moon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { PushSettingsCard } from "@/components/PushNotifications";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_META = {
  inapp: { icon: Bell,          label: "No app",  color: "text-blue-600" },
  email: { icon: Mail,          label: "E-mail",  color: "text-emerald-600" },
  sms:   { icon: MessageSquare, label: "SMS",     color: "text-purple-600" },
};

export default function NotificationPreferences({ compact = false }) {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [prefs, setPrefs] = useState({});
  const [quiet, setQuiet] = useState({ enabled: false, start: "22:00", end: "08:00", timezone: "America/Sao_Paulo" });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/notifications/preferences`);
      setData(r.data);
      setPrefs(r.data.prefs || {});
      if (r.data.quiet_hours) setQuiet(r.data.quiet_hours);
      setDirty(false);
    } catch (e) {
      toast.error("Falha ao carregar preferências");
    }
  };

  useEffect(() => { load(); }, []);

  const setChannel = (eventKey, channel, value) => {
    setPrefs((prev) => ({
      ...prev,
      [eventKey]: { ...(prev[eventKey] || {}), [channel]: value },
    }));
    setDirty(true);
  };

  const setQuietField = (k, v) => {
    setQuiet((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/notifications/preferences`, { prefs, quiet_hours: quiet });
      setPrefs(r.data.prefs);
      if (r.data.quiet_hours) setQuiet(r.data.quiet_hours);
      setDirty(false);
      toast.success("Preferências salvas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  const sendTest = async (eventKey) => {
    setTesting(eventKey);
    try {
      await axios.post(`${API}/notifications/test`, { event_key: eventKey });
      toast.success("Notificação de teste enviada. Verifique os canais ativos.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível enviar teste");
    }
    setTesting("");
  };

  const total = useMemo(() => {
    if (!prefs || !data?.events) return { events: 0, on: 0 };
    let on = 0;
    for (const ev of data.events) {
      const p = prefs[ev.key] || {};
      if (p.inapp || p.email || p.sms) on += 1;
    }
    return { events: data.events.length, on };
  }, [prefs, data]);

  if (!data) {
    return (
      <Card className="p-6 flex items-center gap-2" data-testid="notif-prefs-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando preferências…
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="notif-prefs-root">
      <PushSettingsCard />
      {!compact && (
        <Card className="p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 border-primary/20">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold tracking-tight">Notificações multi-canal</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Escolha em quais canais você quer receber cada categoria de aviso. Você pode enviar um teste a qualquer momento.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" data-testid="notif-summary-count">
              {total.on}/{total.events} categorias ativas
            </Badge>
            {!data.email_configured && (
              <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="notif-email-mocked">
                E-mail MOCKED
              </Badge>
            )}
            {data.sms_mocked && (
              <Badge variant="outline" className="border-purple-300 text-purple-700" data-testid="notif-sms-mocked">
                SMS MOCKED
              </Badge>
            )}
          </div>
        </Card>
      )}

      {compact && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="notif-summary-compact">
          <Badge variant="secondary" data-testid="notif-summary-count-compact">
            {total.on}/{total.events} categorias ativas
          </Badge>
          {!data.email_configured && (
            <Badge variant="outline" className="border-amber-300 text-amber-700" data-testid="notif-email-mocked-compact">
              E-mail MOCKED
            </Badge>
          )}
          {data.sms_mocked && (
            <Badge variant="outline" className="border-purple-300 text-purple-700" data-testid="notif-sms-mocked-compact">
              SMS MOCKED
            </Badge>
          )}
        </div>
      )}

      {(!data.user_email || !data.user_phone) && (
        <Card className="p-4 flex items-start gap-3 border-amber-200 bg-amber-50/60" data-testid="notif-contact-warning">
          <Info className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="text-xs text-amber-800">
            {!data.user_email && <div>• Cadastre um <strong>e-mail</strong> no perfil para receber avisos por e-mail.</div>}
            {!data.user_phone && <div>• Cadastre um <strong>telefone</strong> no perfil para receber SMS.</div>}
          </div>
        </Card>
      )}

      {/* Quiet hours */}
      <Card className="p-5" data-testid="notif-quiet-hours">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center shrink-0">
            <Moon className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-sm">Modo silencioso</div>
                <div className="text-[11px] text-muted-foreground">
                  Durante a janela abaixo, e-mail e SMS ficam em silêncio. Você ainda vê tudo no sino do app.
                </div>
              </div>
              <Switch
                checked={!!quiet.enabled}
                onCheckedChange={(v) => setQuietField("enabled", v)}
                data-testid="notif-quiet-enabled"
                aria-label="Ativar modo silencioso"
              />
            </div>
            {quiet.enabled && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Início</span>
                  <Input
                    type="time"
                    value={quiet.start || "22:00"}
                    onChange={(e) => setQuietField("start", e.target.value)}
                    data-testid="notif-quiet-start"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Fim</span>
                  <Input
                    type="time"
                    value={quiet.end || "08:00"}
                    onChange={(e) => setQuietField("end", e.target.value)}
                    data-testid="notif-quiet-end"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Fuso</span>
                  <Input
                    value={quiet.timezone || "America/Sao_Paulo"}
                    onChange={(e) => setQuietField("timezone", e.target.value)}
                    placeholder="America/Sao_Paulo"
                    data-testid="notif-quiet-timezone"
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden" data-testid="notif-prefs-table">
        <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
          <div className="col-span-6">Evento</div>
          <div className="col-span-1 text-center">No app</div>
          <div className="col-span-1 text-center">E-mail</div>
          <div className="col-span-1 text-center">SMS</div>
          <div className="col-span-3 text-right">Teste</div>
        </div>
        <ul className="divide-y divide-border">
          {data.events.map((ev) => {
            const p = prefs[ev.key] || {};
            return (
              <li key={ev.key} className="px-5 py-4 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-2 md:items-center" data-testid={`notif-row-${ev.key}`}>
                <div className="md:col-span-6">
                  <div className="font-semibold text-sm">{ev.label}</div>
                  <div className="text-[11px] text-muted-foreground">{ev.description}</div>
                </div>
                {(["inapp", "email", "sms"]).map((ch) => {
                  const Meta = CHANNEL_META[ch];
                  const Icon = Meta.icon;
                  return (
                    <div key={ch} className="md:col-span-1 flex items-center gap-2 md:justify-center">
                      <Icon className={`w-3.5 h-3.5 md:hidden ${Meta.color}`} />
                      <span className="text-xs md:hidden">{Meta.label}</span>
                      <Switch
                        checked={!!p[ch]}
                        onCheckedChange={(v) => setChannel(ev.key, ch, v)}
                        data-testid={`notif-toggle-${ev.key}-${ch}`}
                        aria-label={`${ev.label} · ${Meta.label}`}
                      />
                    </div>
                  );
                })}
                <div className="md:col-span-3 flex md:justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full gap-1"
                    onClick={() => sendTest(ev.key)}
                    disabled={testing === ev.key || (!p.inapp && !p.email && !p.sms)}
                    data-testid={`notif-test-${ev.key}`}
                  >
                    {testing === ev.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Enviar teste
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <div className="flex items-center justify-between gap-2 sticky bottom-2 md:static bg-background/95 backdrop-blur md:bg-transparent md:backdrop-blur-none rounded-full md:rounded-none px-4 py-3 md:p-0 border md:border-0 shadow md:shadow-none">
        <div className="text-[11px] text-muted-foreground">
          {dirty ? "Você tem alterações não salvas" : "Tudo salvo"}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={load} disabled={saving} data-testid="notif-reset">
            Descartar
          </Button>
          <Button className="rounded-full gap-1" onClick={save} disabled={!dirty || saving} data-testid="notif-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Salvar preferências
          </Button>
        </div>
      </div>
    </div>
  );
}
